
/* Native Impact slideshow; only controls media resource loading and playback. */
(() => {
  const selector = 'slideshow-carousel[id^="async-hotspot-"]';
  const instances = new WeakMap();

  function init(carousel) {
    if (instances.has(carousel)) return;

    const slides = [...carousel.querySelectorAll('.slideshow__slide')];
    if (!slides.length) return;

    const lead = Number(carousel.dataset.vhaLead) || 5;
    const state = { active: -1, disposed: false, cleanup: [] };
    instances.set(carousel, state);

    const videos = slide => [...slide.querySelectorAll('video')];
    const usable = slide => videos(slide).filter(
      v => getComputedStyle(v).display !== 'none'
    );
    const allSources = v => [...v.querySelectorAll('source')];

    function load(slide) {
      for (const v of usable(slide)) {
        let changed = false;

        for (const source of allSources(v)) {
          if (source.dataset.vhaSrc && !source.hasAttribute('src')) {
            source.src = source.dataset.vhaSrc;
            changed = true;
          }
        }

        if (changed) v.load();
      }
    }

    function stop(slide) {
      for (const v of videos(slide)) v.pause();
    }

    function play(slide) {
      if (
        document.hidden ||
        matchMedia('(prefers-reduced-motion: reduce)').matches
      ) return;

      for (const v of usable(slide)) {
        v.play().catch(() => {});
      }
    }

    function sync() {
      if (state.disposed || !carousel.isConnected) return;

      let idx = slides.findIndex(
        s => s.classList.contains('is-selected')
      );

      if (idx < 0) idx = 0;

      if (idx !== state.active) {
        if (state.active >= 0) stop(slides[state.active]);

        state.active = idx;
        load(slides[idx]);
        play(slides[idx]);
      }
    }

    function prefetch() {
      const idx = state.active;
      if (idx < 0 || slides.length < 2) return;

      load(slides[(idx + 1) % slides.length]);
    }

    slides.forEach((slide, idx) => {
      for (const v of videos(slide)) {
        const onTime = () => {
          if (
            idx !== state.active ||
            !Number.isFinite(v.duration)
          ) return;

          if (v.duration - v.currentTime <= lead) {
            prefetch();
          }
        };

        const onEnd = () => {
          if (idx !== state.active || document.hidden) return;

          if (typeof carousel.next === 'function') {
            carousel.next();
          } else {
            carousel.select?.((idx + 1) % slides.length);
          }
        };

        v.addEventListener('timeupdate', onTime);
        v.addEventListener('ended', onEnd);

        state.cleanup.push(() => {
          v.removeEventListener('timeupdate', onTime);
          v.removeEventListener('ended', onEnd);
        });
      }
    });

    const observer = new MutationObserver(sync);

    slides.forEach(slide => {
      observer.observe(slide, {
        attributes: true,
        attributeFilter: ['class']
      });
    });

    state.cleanup.push(() => observer.disconnect());

    const onVisibility = () => {
      if (state.active < 0) return;

      if (document.hidden) {
        stop(slides[state.active]);
      } else {
        play(slides[state.active]);
      }
    };

    document.addEventListener('visibilitychange', onVisibility);

    state.cleanup.push(() =>
      document.removeEventListener('visibilitychange', onVisibility)
    );

    const onChange = () => {
      for (const slide of slides) stop(slide);
      state.active = -1;
      sync();
    };

    matchMedia('(max-width: 639px)').addEventListener?.(
      'change', onChange
    );

    state.cleanup.push(() =>
      matchMedia('(max-width: 639px)').removeEventListener?.(
        'change', onChange
      )
    );

    sync();

    carousel.__vhaDispose = () => {
      state.disposed = true;
      state.cleanup.forEach(fn => fn());
      instances.delete(carousel);
    };
  }

  function scan(scope = document) {
    scope.querySelectorAll(selector).forEach(init);
  }

  scan();

  document.addEventListener('shopify:section:load', e =>
    scan(e.target)
  );

  document.addEventListener('shopify:section:unload', e =>
    e.target.querySelectorAll(selector).forEach(
      c => c.__vhaDispose?.()
    )
  );
})();
